import { useEffect, useState } from 'react'
import { ArrowRight, Loader2, PartyPopper } from 'lucide-react'

import {
  buyLifetime,
  getCheckoutProviderLabel,
  getPortalUrl,
  hasExistingPurchase,
  isCheckoutConfigured,
  LAUNCH_CTA_LABEL,
} from '../lib/purchases'
import { isGiftCheckoutRequest } from '../lib/giftCheckout'

type BuyState = 'idle' | 'loading' | 'purchased' | 'error'

interface BuyButtonProps {
  size?: 'md' | 'lg'
  label?: string
  className?: string
}

/**
 * The single CTA of the funnel: opens RevenueCat checkout directly (no
 * sign-up first), then sends the buyer to the app portal to create their
 * account and download the Mac app.
 */
export function BuyButton({ size = 'lg', label, className = '' }: BuyButtonProps) {
  const [state, setState] = useState<BuyState>('idle')
  const [error, setError] = useState('')
  const checkoutProvider = getCheckoutProviderLabel()

  useEffect(() => {
    let cancelled = false
    hasExistingPurchase().then((purchased) => {
      if (!cancelled && purchased) setState('purchased')
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleBuy = async () => {
    if (state === 'loading') return

    if (!isCheckoutConfigured()) {
      // Checkout not wired yet (no RevenueCat key), so fall back to the portal,
      // which hosts the same purchase flow after onboarding.
      window.location.href = getPortalUrl()
      return
    }

    setState('loading')
    setError('')
    try {
      const params = new URLSearchParams(window.location.search)
      const gift = isGiftCheckoutRequest(window.location.search)
      // Stripe's gift checkout deliberately exposes its promotion-code field.
      // The code itself stays in the recipient's hands, never in this request.
      const { portalUrl } = await buyLifetime(gift ? 'gift' : (params.get('discount_code') ?? undefined))
      setState('purchased')
      window.location.href = portalUrl
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      // User closing the checkout modal isn't an error worth surfacing
      if (/cancel|closed|abort/i.test(message)) {
        setState('idle')
      } else {
        setState('error')
        setError('Checkout could not start. Please try again.')
      }
    }
  }

  if (state === 'purchased') {
    return (
      <a
        href={getPortalUrl()}
        className={`inline-flex items-center justify-center gap-2 rounded-brand bg-brand-gradient font-semibold text-white shadow-lg shadow-brand/30 transition-transform hover:scale-[1.03] active:scale-95 ${
          size === 'lg' ? 'px-8 py-4 text-lg' : 'px-6 py-3 text-base'
        } ${className}`}
      >
        <PartyPopper className="h-5 w-5" />
        You own it. Get the app
        <ArrowRight className="h-5 w-5" />
      </a>
    )
  }

  return (
    <div className={`inline-flex flex-col items-center gap-2 ${className}`}>
      <button
        onClick={handleBuy}
        disabled={state === 'loading'}
        className={`inline-flex items-center justify-center gap-2 rounded-brand bg-brand-gradient font-semibold text-white shadow-lg shadow-brand/30 transition-transform hover:scale-[1.03] active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 ${
          size === 'lg' ? 'px-8 py-4 text-lg' : 'px-6 py-3 text-base'
        }`}
      >
        {state === 'loading' ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Opening {checkoutProvider}…
          </>
        ) : (
          <>
            {isGiftCheckoutRequest(typeof window === 'undefined' ? '' : window.location.search)
              ? 'Redeem your gift code'
              : (label ?? LAUNCH_CTA_LABEL)}
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </button>
      {state === 'error' && <p className="text-sm font-medium text-red-400">{error}</p>}
    </div>
  )
}
